import {
  HHChip,
  HHIconButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import Close from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Modal from '@mui/material/Modal';
import Switch from '@mui/material/Switch';
import TablePagination from '@mui/material/TablePagination';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
  DataGrid,
  GridColDef,
  GridComparatorFn,
  GridRenderCellParams,
  GridRowsProp,
  GridValidRowModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { useCallback, useState } from 'react';
import { ActiveContractLabels } from '../../constants/strings/billing-label-enums';
import { routes } from '../../constants/strings/routes';
import {
  ContractTemplateType,
  ContractType,
  useGetContractsV2Query,
  useGetPartnershipsQuery,
} from '../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../utils/bills-utils';
import {
  ActiveStatus,
  calculateChipColor,
  computeActiveStatus,
  dateSorterFunction,
  extractProductPackages,
  getMostExpensiveBillingModelName,
  sortContracts,
} from '../../utils/contract-utils';
import { computeEffectiveRange } from '../../utils/date-helper';
import { IDiscount } from '../../utils/discount-utils';
import LoadingComponent from '../utils/loading';
import { EditContractButton } from './billing-edit-contract-button';
import DisplayContractDetails from './billing-tool-contract-grid-display';
import ContractDetailView from './contract-view-components/contract-detail-view';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 5;

const useStyles = makeStyles(theme =>
  createStyles({
    gridStyles: {
      display: 'flex',
      marginTop: '1em',
    },
    oddRow: {
      backgroundColor: theme.palette.background.default,
    },
    evenRow: {
      backgroundColor: theme.palette.background.paper,
    },
    modalStyles: {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '60vw',
      height: '50vh',
      backgroundColor: '#fff',
      border: '2px solid grey',
      padding: '2em',
      overflow: 'hidden',
    },
  }),
);

export const CONTRACT_GRID_TEST_ID = 'contract-grid-test-id';

interface ContractGridProps {
  clientInsurerId: number;
  templates: ContractTemplateType[];
  discounts?: IDiscount[];
}
type ContractDateRange = Pick<ContractType, 'startDate' | 'endDate'>;

const effectiveDatesComparator: GridComparatorFn<ContractDateRange> = (
  v1,
  v2,
) => dateSorterFunction(v1.startDate, v2.startDate);

const ContractGrid = ({
  clientInsurerId,
  templates,
  discounts,
}: ContractGridProps): JSX.Element | null => {
  const classes = useStyles();
  const [isModelOpen, setIsModelOpen] = useState<boolean>(false);
  const [contractIdToView, setContractIdToView] = useState<number | null>(null);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [hideVoided, setHideVoided] = useState(true);

  const { data: contractData, loading: contractLoading } =
    useGetContractsV2Query({
      fetchPolicy: 'cache-and-network',
      variables: {
        clientsInsurerIds: [clientInsurerId],
        includeVoided: !hideVoided,
        page,
        size: pageSize,
      },
    });

  const { data: partnershipData, error: partnershipError } =
    useGetPartnershipsQuery({
      variables: { type: 'billing' },
    });

  const data = sortContracts(contractData?.getContractsV2?.items ?? []);
  const totalContracts = contractData?.getContractsV2?.total ?? 0;

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(DEFAULT_PAGE);
  }, []);

  const handleHideVoidedChange = useCallback((checked: boolean) => {
    setHideVoided(checked);
    setPage(DEFAULT_PAGE);
  }, []);

  if (!templates) {
    return <LoadingComponent />;
  }

  if (contractLoading && !contractData) {
    return <LoadingComponent />;
  }

  const rows: GridRowsProp = data.map(item => ({
    id: item.id,
    template:
      templates.find(({ id }) => id === item.contractTemplateId)?.name ??
      'No matching template id',
    contractType:
      templates.find(({ id }) => id === item.contractTemplateId)?.name ??
      getMostExpensiveBillingModelName(item.contractPackages) ??
      'No matching contract type',
    contractPackages: extractProductPackages(item.contractPackages),
    chronicPrice: item.chronicPrice,
    acutePrice: item.acutePrice,
    inPersonVisitPrice: item.contractPackages[0]?.price,
    effectiveDates: {
      startDate: item.startDate,
      endDate: item.endDate,
    },
    billingType: item.billingType ?? '--',
    status: computeActiveStatus(item.void, item.startDate, item.endDate),
    packageEnabled: item.packageEnabled,
    partnership: partnershipError
      ? 'Unable to get partnership'
      : (partnershipData?.getPartnerships.find(p => p.id === item.partnershipId)
          ?.name ?? '--'),
  }));

  const columns: GridColDef<GridValidRowModel>[] = [
    {
      field: 'contractType',
      headerName: 'Contract Type',
      flex: 1,
    },
    {
      field: 'partnership',
      headerName: 'Partnership',
      flex: 1,
    },
    {
      field: 'prices',
      headerName: 'Prices',
      flex: 1,
      renderCell: (params: GridRenderCellParams): JSX.Element => (
        <DisplayContractDetails
          templateName={params.row.template}
          chronicPrice={params.row.chronicPrice}
          acutePrice={params.row.acutePrice}
          inPersonVisitPrice={params.row.inPersonVisitPrice}
          contractPackages={params.row.contractPackages}
          packageEnabled={params.row.packageEnabled}
          currency={
            data.find(contract => contract.id === params.row.id)?.currency ??
            'USD'
          }
        />
      ),
    },
    {
      field: 'effectiveDates',
      headerName: 'Effective Dates',
      flex: 1.2,
      minWidth: 100,
      sortComparator: effectiveDatesComparator,
      valueFormatter: ({
        value,
      }: GridValueFormatterParams<ContractDateRange>): string =>
        computeEffectiveRange(value.startDate, value.endDate),
    },
    {
      field: 'billingType',
      headerName: 'Billing Type',
      flex: 1,
      renderCell: (params: GridRenderCellParams): JSX.Element => (
        <HHChip
          hhVariant="variant-bypass"
          color="info"
          size="small"
          label={capitalizeFirstLetterAndSpaceSnakeString(
            params.row.billingType ?? 'None',
          )}
          title={capitalizeFirstLetterAndSpaceSnakeString(
            params.row.billingType ?? 'None',
          )}
        />
      ),
    },
    {
      flex: 0,
      sortable: false,
      field: 'status',
      headerName: 'Status',
      align: 'left',
      headerAlign: 'left',
      renderCell: (
        params: GridRenderCellParams<GridValidRowModel, ActiveStatus>,
      ): JSX.Element => (
        <HHStack direction="row" spacing={1}>
          <HHChip
            hhVariant="variant-bypass"
            color={calculateChipColor(params.value)}
            size="small"
            label={params.value}
          />
        </HHStack>
      ),
    },
    {
      flex: 0,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      field: 'actions',
      minWidth: 125,
      headerName: '',
      renderCell: (params: GridRenderCellParams): JSX.Element => (
        <HHStack flexWrap="wrap" direction="row" spacing={1}>
          {params.row.status !== ActiveStatus.Void &&
          params.row.packageEnabled ? (
            <EditContractButton
              isText={true}
              contractId={parseInt(String(params.id), 10)}
              route={routes.billing.editContractPackageContract}
            />
          ) : null}
          <HHIconButton
            hhVariant="variant-bypass"
            aria-label="View"
            size="small"
            color="primary"
            title="View"
            onClick={(): void => {
              setContractIdToView(parseInt(String(params.id), 10));
              setIsModelOpen(true);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </HHIconButton>
        </HHStack>
      ),
    },
  ];

  const closeModal = (): void => {
    setContractIdToView(null);
    setIsModelOpen(false);
  };

  return rows.length === 0 ? null : (
    <Box>
      <HHStack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <HHTypography hhVariant="h2">
          {ActiveContractLabels.CONTRACTS_LABEL}
        </HHTypography>
        <FormControlLabel
          control={
            <Switch
              checked={hideVoided}
              onChange={(_event, checked): void =>
                handleHideVoidedChange(checked)
              }
              inputProps={{ 'aria-label': 'Hide voided contracts' }}
            />
          }
          label="Hide voided"
        />
      </HHStack>
      <Box className={classes.gridStyles} data-testid={CONTRACT_GRID_TEST_ID}>
        <DataGrid
          rows={rows}
          columns={columns}
          paginationModel={{ page: 0, pageSize }}
          disableRowSelectionOnClick
          hideFooter
          autoHeight
          disableVirtualization
          sx={{
            border: 0,
            '& .MuiDataGrid-iconSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: (theme): string => theme.palette.action.focus,
            },
          }}
          getRowClassName={(params): string =>
            params.indexRelativeToCurrentPage % 2 === 0
              ? classes.evenRow
              : classes.oddRow
          }
        />
      </Box>
      <HHStack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        sx={{
          borderTop: '1px solid rgba(224, 224, 224, 1)',
        }}
      >
        <TablePagination
          component="div"
          count={totalContracts}
          page={page - 1}
          onPageChange={(_event, newPage): void => {
            handlePageChange(newPage + 1);
          }}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(event): void => {
            handlePageSizeChange(parseInt(event.target.value, 10));
          }}
          rowsPerPageOptions={[5, 10, 20]}
          labelRowsPerPage="Contracts per page:"
        />
      </HHStack>
      <Modal
        open={isModelOpen}
        onClose={(): void => closeModal()}
        closeAfterTransition
      >
        <Box className={classes.modalStyles}>
          <HHStack
            direction="column"
            justifyContent="flex-end"
            height="100%"
            spacing={4}
          >
            <HHStack flex={1} direction="column" spacing={4} overflow="hidden">
              <HHStack flex={0} direction="row" justifyContent="space-between">
                <HHTypography hhVariant="section-title">
                  Contract Details
                </HHTypography>
                <HHIconButton
                  hhVariant="variant-bypass"
                  size="medium"
                  color="default"
                  aria-label="close"
                  onClick={(): void => closeModal()}
                >
                  <Close />
                </HHIconButton>
              </HHStack>
              <HHStack direction="column" overflow="auto">
                <ContractDetailView
                  templates={templates}
                  contract={data.find(({ id }) => id === contractIdToView)}
                  showJson
                  discounts={discounts}
                />
              </HHStack>
            </HHStack>
            {data.find(({ id }) => id === contractIdToView)?.void === false &&
            data.find(({ id }) => id === contractIdToView)?.packageEnabled ===
              true ? (
              <HHStack flex={0} direction="row">
                <EditContractButton
                  isText={false}
                  contractId={contractIdToView ?? 0}
                  route={routes.billing.editContractPackageContract}
                />
              </HHStack>
            ) : null}
          </HHStack>
        </Box>
      </Modal>
    </Box>
  );
};

export default ContractGrid;
